import React from 'react';

import { graphql, PageProps } from 'gatsby';

import { Grid2 as Grid, Link, styled } from '@mui/material';

import { LegalPageType } from '@mewa/types';
import { MarkdownContainer, ResponsiveGrid } from '@mewa/ui';

import { GlobalPageContext } from '../types';
import { DownloadItem } from './press/types';

export { Head } from '../layouts/head';

type LegalMarkdownPageType = {
  content: {
    data: {
      childMarkdownRemark: { html: string };
    };
  };
  name: string;
};

type Props = {
  strapiImprint: LegalMarkdownPageType;
  strapiPrivacyPolicy: LegalMarkdownPageType;
  strapiPrivacyPolicySurvey: LegalMarkdownPageType;
  strapiPrivacyPolicySocialMedia: LegalMarkdownPageType;
  strapiPrivacyPolicyBusinessContact: LegalMarkdownPageType;
  strapiSurveyVoiceOfCustomer: LegalMarkdownPageType;
  strapiSupplyChainAct: LegalMarkdownPageType & {
    downloads: DownloadItem[] | null;
  };
};

type PageContext = GlobalPageContext & {
  id: string;
  legalType: LegalPageType;
};

export const query = graphql`
  query ($id: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment

    strapiImprint(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
    }

    strapiPrivacyPolicy(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
    }

    strapiPrivacyPolicySurvey(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
    }

    strapiPrivacyPolicySocialMedia(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
    }

    strapiPrivacyPolicyBusinessContact(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
    }

    strapiSurveyVoiceOfCustomer(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
    }

    strapiSupplyChainAct(id: { eq: $id }) {
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      name
      downloads {
        fields {
          assetUrl
        }
        extension: ext
        name
        mime
        height
        width
      }
    }
  }
`;

export const LegalMarkdownPage = ({
  data: {
    strapiImprint,
    strapiPrivacyPolicy,
    strapiPrivacyPolicySurvey,
    strapiPrivacyPolicySocialMedia,
    strapiPrivacyPolicyBusinessContact,
    strapiSupplyChainAct,
    strapiSurveyVoiceOfCustomer,
  },
  pageContext,
}: PageProps<Props, PageContext>) => {
  let html = '';
  let downloads: DownloadItem[] = [];

  if (pageContext.legalType === LegalPageType.imprint) {
    html = strapiImprint.content.data.childMarkdownRemark.html;
  } else if (pageContext.legalType === LegalPageType.privacyPolicy) {
    html = strapiPrivacyPolicy.content.data.childMarkdownRemark.html;
  } else if (pageContext.legalType === LegalPageType.privacyPolicySurvey) {
    html = strapiPrivacyPolicySurvey.content.data.childMarkdownRemark.html;
  } else if (pageContext.legalType === LegalPageType.privacyPolicySocialMedia) {
    html = strapiPrivacyPolicySocialMedia.content.data.childMarkdownRemark.html;
  } else if (
    pageContext.legalType === LegalPageType.privacyPolicyBusinessContact
  ) {
    html =
      strapiPrivacyPolicyBusinessContact.content.data.childMarkdownRemark.html;
  } else if (pageContext.legalType === LegalPageType.supplyChainAct) {
    html = strapiSupplyChainAct.content.data.childMarkdownRemark.html;
    downloads = strapiSupplyChainAct.downloads ?? [];
  } else if (pageContext.legalType === LegalPageType.surveyVoiceOfCustomer) {
    html = strapiSurveyVoiceOfCustomer.content.data.childMarkdownRemark.html;
  }

  return (
    <ResponsiveGrid>
      <Grid
        size={{
          xs: 11,
          md: 9,
          lg: 8,
        }}
      >
        <MarkdownContainer html={html} />
        {downloads && downloads.length > 0 ? (
          <>
            <Headline2>Downloads</Headline2>
            <DownloadList>
              {downloads.map((downloadItem, index) => (
                <li key={index}>
                  <Link
                    color="primary"
                    underline="hover"
                    href={downloadItem.fields.assetUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {downloadItem.name}
                  </Link>
                </li>
              ))}
            </DownloadList>
          </>
        ) : null}
      </Grid>
    </ResponsiveGrid>
  );
};

const Headline2 = styled('h2')(({ theme }) => ({
  ...theme.typography.h4,
}));

const DownloadList = styled('ul')({
  marginBottom: '100px',
  padding: 0,

  listStyle: 'none',
});

LegalMarkdownPage.noContactTeaser = true;

export default LegalMarkdownPage;
